import React, { useState } from "react";
import { AppSettingFieldSchema } from "../../../types/appSettings";

interface SchemaFieldProps {
  field: AppSettingFieldSchema;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  isDarkMode: boolean;
}

// Blur on wheel so scrolling over a focused number input cannot mutate its value;
// admins must type the value explicitly. Matches the pattern in the strategy editors.
const preventScrollChange = (e: React.WheelEvent<HTMLInputElement>) => {
  (e.target as HTMLInputElement).blur();
};

const SchemaField: React.FC<SchemaFieldProps> = ({
  field,
  value,
  onChange,
  isDarkMode,
}) => {
  const [tagInput, setTagInput] = useState("");

  const isNullable = field.type.includes("null");
  const isArray = field.type.includes("[]");

  const baseInputClass = `w-full rounded-md border px-3 py-2 text-sm font-mono outline-none transition-colors ${
    isDarkMode
      ? "bg-rp-surface-sunken border-rp-separator text-rp-grey focus:border-rp-amber"
      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
  }`;

  const hintClass = `text-xs mt-1 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`;

  const buildHint = () => {
    const parts: string[] = [];
    if (field.min !== undefined) parts.push(`min: ${field.min}`);
    if (field.default !== undefined) parts.push(`default: ${JSON.stringify(field.default)}`);
    return parts.join(" · ");
  };

  const labelEl = (
    <label
      className={`text-xs font-medium flex items-center gap-1.5 mb-1.5 ${
        isDarkMode ? "text-rp-grey" : "text-gray-700"
      }`}
    >
      {field.name}
      <span
        className={`text-[10px] font-mono px-1 rounded ${
          isDarkMode ? "bg-rp-surface-sunken text-rp-grey/50" : "bg-gray-100 text-gray-500"
        }`}
      >
        {field.type}
      </span>
      {field.required && <span className="text-red-500 font-bold">*</span>}
    </label>
  );

  // --- Scalar number/integer ---
  if (!isArray) {
    return (
      <div>
        {labelEl}
        <input
          type="number"
          className={baseInputClass}
          value={value as number ?? ""}
          step={field.integer ? 1 : 0.1}
          min={field.min}
          onWheel={preventScrollChange}
          onChange={(e) =>
            onChange(
              field.name,
              e.target.value === "" ? "" : field.integer ? parseInt(e.target.value) : parseFloat(e.target.value)
            )
          }
        />
        {buildHint() && <div className={hintClass}>{buildHint()}</div>}
      </div>
    );
  }

  // --- Array (possibly nullable) ---
  const arrayValue = value === null ? null : (Array.isArray(value) ? value : []);
  const isNull = value === null;

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const trimmed = tagInput.trim();
    if (!trimmed || isNaN(Number(trimmed))) return;
    const num = field.type.includes("integer") ? parseInt(trimmed) : parseFloat(trimmed);
    const current = arrayValue ?? [];
    onChange(field.name, [...current, num]);
    setTagInput("");
  };

  const handleRemoveTag = (index: number) => {
    if (!arrayValue) return;
    onChange(field.name, arrayValue.filter((_: unknown, i: number) => i !== index));
  };

  return (
    <div className="col-span-full">
      {labelEl}

      {isNullable && (
        <label
          className={`flex items-center gap-2 text-xs mb-1.5 cursor-pointer ${
            isDarkMode ? "text-rp-grey/50" : "text-gray-500"
          }`}
        >
          <input
            type="checkbox"
            checked={isNull}
            onChange={(e) => onChange(field.name, e.target.checked ? null : [])}
            className="accent-rp-amber"
          />
          Set to null (use default)
        </label>
      )}

      <div
        className={`flex flex-wrap gap-1.5 items-center min-h-[38px] rounded-md border px-3 py-2 ${
          isNull ? "opacity-30 pointer-events-none" : ""
        } ${
          isDarkMode
            ? "bg-rp-surface-sunken border-rp-separator focus-within:border-rp-amber"
            : "bg-white border-gray-300 focus-within:border-blue-500"
        }`}
      >
        {(arrayValue ?? []).map((v: number, i: number) => (
          <span
            key={i}
            className={`inline-flex items-center gap-1 text-xs font-mono rounded px-2 py-0.5 border ${
              isDarkMode
                ? "bg-rp-surface-raised border-rp-separator"
                : "bg-gray-100 border-gray-200"
            }`}
          >
            {v}
            <span
              className={`cursor-pointer leading-none ${
                isDarkMode ? "text-rp-grey/50 hover:text-red-500" : "text-gray-400 hover:text-red-500"
              }`}
              onClick={() => handleRemoveTag(i)}
            >
              &times;
            </span>
          </span>
        ))}
        <input
          className={`flex-1 min-w-[80px] bg-transparent border-none outline-none text-xs font-mono ${
            isDarkMode ? "text-rp-grey placeholder:text-rp-grey/30" : "text-gray-900 placeholder:text-gray-400"
          }`}
          placeholder="Type a number and press Enter..."
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleAddTag}
        />
      </div>
      {buildHint() && <div className={hintClass}>{buildHint()}</div>}
    </div>
  );
};

export default SchemaField;
